import { useState, useMemo, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import styles from "./neighbourhoodSection.module.scss";
import { NeighbourhoodTab } from "./types";
import { buildNeighbourhoodData } from "./utils";
import { applyBackendSentences, hasBackendSentences } from "./utils/backendSentences";
import {
  selectNeighbourhoodPayload,
  selectNearbyDistances,
  selectCountryShortCode,
  selectPropertyReadyStatus,
  isPropertyFetchInFlight,
} from "./utils/selectors";
import { resolveCopy } from "./utils/neighbourhoodHelpers";
import CostOfLivingTab from "./components/CostOfLivingTab";
import GettingAroundTab from "./components/GettingAroundTab";
// import SafetyTab from "./components/SafetyTab";
import CommunityTab from "./components/CommunityTab";
import ClimateTab from "./components/ClimateTab";
import NeighbourhoodSkeleton from "./components/NeighbourhoodSkeleton";

const TAB_ORDER: readonly NeighbourhoodTab[] = [
  "costOfLiving",
  "gettingAround",
  // "safety",
  "community",
  "climate",
] as const;

// The specific fact-row categories that count toward each tab's render gate, plus
// the minimum number of those rows that must have data for the tab to render.
// Rows outside these lists (e.g. Safety's police-officers) never count toward the gate.
const TAB_QUALIFICATION: Record<NeighbourhoodTab, { minRows: number; keys?: readonly string[] }> = {
  // Rent + transit + groceries — all three must be present.
  costOfLiving: {
    minRows: 3,
    keys: [
      "propertypage.components.neighbourhood.costOfLiving.fact.rent",
      "propertypage.components.neighbourhood.costOfLiving.fact.transport",
      "propertypage.components.neighbourhood.costOfLiving.fact.groceries",
    ],
  },
  // Walkability + commute — both required (counted from cost_of_living.transit).
  gettingAround: { minRows: 2 },
  // Violent crime + property crime — both required.
  safety: {
    minRows: 2,
    keys: [
      "propertypage.components.neighbourhood.safety.fact.violentCrime",
      "propertypage.components.neighbourhood.safety.fact.propertyCrime",
    ],
  },
  // Median age + universities + renters — all three required.
  community: {
    minRows: 3,
    keys: [
      "propertypage.components.neighbourhood.community.fact.medianAge",
      "propertypage.components.neighbourhood.community.fact.universities",
      "propertypage.components.neighbourhood.community.fact.renters",
    ],
  },
  // Winter + summer + peak daylight — all three required.
  climate: {
    minRows: 3,
    keys: [
      "propertypage.components.neighbourhood.climate.fact.winter",
      "propertypage.components.neighbourhood.climate.fact.summer",
      "propertypage.components.neighbourhood.climate.fact.daylight",
    ],
  },
};

const TAB_LABEL_KEYS: Record<NeighbourhoodTab, string> = {
  costOfLiving: "propertypage.components.neighbourhood.tab.costOfLiving",
  gettingAround: "propertypage.components.neighbourhood.tab.gettingAround",
  safety: "propertypage.components.neighbourhood.tab.safety",
  community: "propertypage.components.neighbourhood.tab.community",
  climate: "propertypage.components.neighbourhood.tab.climate",
};

// Distinct analytics action per tab, e.g. Climate → "climate-tab-clicked".
const TAB_GTAG_ACTIONS: Record<NeighbourhoodTab, string> = {
  costOfLiving: "cost-of-living-tab-clicked",
  gettingAround: "getting-around-tab-clicked",
  safety: "safety-tab-clicked",
  community: "community-tab-clicked",
  climate: "climate-tab-clicked",
};

const NeighbourhoodSection = () => {
  const { t } = useTranslationHook();
  const isNeighbourhoodEnabled = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.NEIGHBOURHOOD_SECTION as string,
  );
  const isBackendCopyOn = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.NEIGHBOURHOOD_BACKEND_COPY as string,
  );
  const [selectedTab, setSelectedTab] = useState<NeighbourhoodTab | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const payload = useSelector(selectNeighbourhoodPayload, shallowEqual);
  const nearbyDistances = useSelector(selectNearbyDistances, shallowEqual);
  const countryCode = useSelector(selectCountryShortCode);
  const readyStatus = useSelector(selectPropertyReadyStatus);
  const userLanguage = useSelector(
    (state: AppState) => (state as any).appData?.userLanguage || "en",
  );

  // Price Trend "budget vs city average" %. When present, the rent row uses this
  // (negative = cheaper) compared to the city average, instead of the Numbeo figure.
  const rentBudgetPercent = useSelector((state: AppState) => {
    const trends = state.propertyPageData?.priceTrendsData?.trends;
    if (!Array.isArray(trends)) return null;
    const now = new Date();
    const currentTrend = trends.find(
      (trend: any) =>
        trend?.year_of_date === now.getFullYear() && trend?.month_of_date === now.getMonth() + 1,
    );
    const raw = currentTrend?.cost_difference_percent;
    const num = typeof raw === "string" ? parseFloat(raw) : raw;
    return typeof num === "number" && Number.isFinite(num) ? num : null;
  });

  // Getting Around's walkability + commute rows come from cost_of_living.transit,
  // not from data.gettingAround.facts. Count the ones that have data so the tab is
  // gated on 2 rows just like the others.
  const gettingAroundRowCount = useSelector((state: AppState) => {
    const transit = state.propertyPageData?.data?.region?.neighbourhood?.cost_of_living?.transit;
    const hasWalkability =
      typeof transit?.walkability_score_percent_difference === "number" &&
      transit.walkability_score_percent_difference !== 0;
    const hasCommute =
      typeof transit?.commute_time_score_percent_difference === "number" &&
      transit.commute_time_score_percent_difference !== 0;
    return (hasWalkability ? 1 : 0) + (hasCommute ? 1 : 0);
  });

  // Long-word locales (de/es/fr) need the badge labels capped + truncated on desktop.
  // English and Chinese labels are short, so leave them untouched.
  const isLongLabelLang = userLanguage !== "en" && userLanguage !== "zh";

  // P1D-5540: with the flag on and sentences in the API response, every data-driven
  // string comes from region.neighbourhood. Otherwise the section builds them as before.
  const useBackendCopy = isBackendCopyOn && hasBackendSentences(payload);

  const data = useMemo(() => {
    if (useBackendCopy) {
      const base = buildNeighbourhoodData(payload, nearbyDistances, countryCode, null);
      return base && applyBackendSentences(base, payload);
    }
    return buildNeighbourhoodData(payload, nearbyDistances, countryCode, rentBudgetPercent);
  }, [useBackendCopy, payload, nearbyDistances, countryCode, rentBudgetPercent]);

  const includedTabs = useMemo<NeighbourhoodTab[]>(() => {
    if (!data) return [];
    // A tab renders only when it has at least its required number of qualifying rows.
    // Getting Around's rows (walkability + commute) live outside data.gettingAround.facts,
    // so its count is derived from cost_of_living.transit instead.
    const rowCount = (tab: NeighbourhoodTab): number => {
      if (tab === "gettingAround") {
        return useBackendCopy ? data.gettingAround?.facts.length ?? 0 : gettingAroundRowCount;
      }
      const facts = data[tab]?.facts ?? [];
      const qualifyingKeys = TAB_QUALIFICATION[tab].keys;
      if (!qualifyingKeys) return facts.length;
      return facts.filter((fact) => qualifyingKeys.some((prefix) => fact.key.startsWith(prefix)))
        .length;
    };
    return TAB_ORDER.filter((tab) => rowCount(tab) >= TAB_QUALIFICATION[tab].minRows);
  }, [data, gettingAroundRowCount, useBackendCopy]);

  // Tell SectionNavigation to re-scan the DOM whenever this section appears or
  // disappears, so the "Neighbourhood" tab tracks it. Fires in both directions:
  // the section is code-split and depends on client-fetched region data (so it
  // mounts after the nav has already filtered), and the GrowthBook flag can turn
  // it off at runtime (so the tab must be dropped again).
  const willRender = isNeighbourhoodEnabled && includedTabs.length > 0;
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event("navigation_section_removed"));
  }, [willRender]);

  // GrowthBook flag gate — hide section when flag is off
  if (!isNeighbourhoodEnabled) return null;

  // Requirements 1.5, 13.1, 13.7, 13.8
  if (!data) {
    return mounted && isPropertyFetchInFlight(readyStatus) ? <NeighbourhoodSkeleton /> : null;
  }

  if (includedTabs.length === 0) return null; // Requirements 1.6, 13.11

  const activeTab =
    selectedTab && includedTabs.includes(selectedTab) ? selectedTab : includedTabs[0];

  // Fire a distinct analytics event per neighbourhood tab (e.g. the Climate tab
  // emits "climate-tab-clicked").
  const handleTabClick = (tabId: NeighbourhoodTab) => {
    setSelectedTab(tabId);
    gtagEventEmitter({
      action: TAB_GTAG_ACTIONS[tabId],
      category: PAGE.INVENTORY,
      tab_name: tabId,
      copy_source: useBackendCopy ? "backend" : "frontend",
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "costOfLiving":
        return data.costOfLiving ? <CostOfLivingTab data={data.costOfLiving} /> : null;
      case "gettingAround":
        return data.gettingAround ? <GettingAroundTab data={data.gettingAround} /> : null;
      // case "safety":
      //   return data.safety ? <SafetyTab data={data.safety} /> : null;
      case "community":
        return data.community ? <CommunityTab data={data.community} /> : null;
      case "climate":
        return data.climate ? <ClimateTab data={data.climate} /> : null;
      default:
        return null;
    }
  };

  return (
    <section
      className={formatClassNames(styles.container, isLongLabelLang && styles.longLabelLang)}
      id="neighbourhood"
      data-testid="neighbourhood-section"
    >
      <div className={styles.header}>
        <h2>{resolveCopy(t, "propertypage.components.neighbourhood.section.heading")}</h2>
      </div>

      <div className={styles.tabsWrapper}>
        <div className={styles.tabsList} role="tablist" aria-label="Neighbourhood information tabs">
          {includedTabs.map((tabId) => (
            <button
              key={tabId}
              type="button"
              role="tab"
              aria-selected={activeTab === tabId}
              aria-controls={`neighbourhood-panel-${tabId}`}
              className={formatClassNames(styles.tab, activeTab === tabId && styles.tabActive)}
              onClick={() => handleTabClick(tabId)}
              tabIndex={activeTab === tabId ? 0 : -1}
            >
              {resolveCopy(t, TAB_LABEL_KEYS[tabId])}
            </button>
          ))}
        </div>
      </div>

      <div
        id={`neighbourhood-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`neighbourhood-tab-${activeTab}`}
      >
        {renderTabContent()}
      </div>
    </section>
  );
};

export default wrapperHOC(NeighbourhoodSection, {
  componentName: "NeighbourhoodSection",
  showForChina: false,
  showForWhiteLable: false,
});
